import React from "react";
import { Tooltip, TooltipBadgeContent } from "./Tooltip";

// Derives a single roll-up status for an MR used by the colored dot that
// replaces the author avatar in the card header.
//
// - green:  all CI checks passed AND fully approved
// - red:    CI is failing
// - yellow: unresolved comments while in review, OR not yet approved with no open comments
// - gray:   anything else still in progress (e.g. pipeline running)
export const getMRStatus = (mr) => {
	const pipeline = mr?.headPipeline;
	const failedCI =
		!!pipeline &&
		(pipeline.status === "failed" || (pipeline.failedJobs || 0) > 0);
	const passing = pipeline?.status === "success";
	const approved = mr?.approvalsLeft === 0;
	const hasComments = (mr?.discussions || 0) > 0;
	const inReview = !!mr?.hasHumanReview;

	if (failedCI) {
		return {
			color: "red",
			label: "Failing CI",
			description: "Pipeline has failing checks.",
		};
	}

	if (passing && approved) {
		return {
			color: "green",
			label: "Ready",
			description: "All checks passed and approved.",
		};
	}

	if (hasComments && inReview) {
		return {
			color: "yellow",
			label: "Unresolved comments",
			description: "Open comments while in review.",
		};
	}

	if (!approved && !hasComments) {
		return {
			color: "yellow",
			label: "Awaiting review",
			description: "Not yet approved, no open comments.",
		};
	}

	return {
		color: "gray",
		label: "In progress",
		description: "CI or review still in progress.",
	};
};

const colorClasses = {
	green: "bg-[var(--color-row-green)]",
	red: "bg-rose-500",
	yellow: "bg-amber-400",
	gray: "bg-slate-400",
};

export const MRStatusDot = ({ mr, className = "", wrapperClassName = "" }) => {
	if (!mr) return null;

	const status = getMRStatus(mr);
	const authorName = mr.author?.name || "Unknown Author";

	return (
		<Tooltip
			className={wrapperClassName || "shrink-0 self-center items-center"}
			content={
				<TooltipBadgeContent badge={status.label}>
					{status.description}
					<span className="block text-[var(--color-text-secondary)]">
						{authorName}
					</span>
				</TooltipBadgeContent>
			}
		>
			<span
				className={`block h-2 w-2 flex-shrink-0 rounded-full ${colorClasses[status.color]} ${className}`}
				aria-label={status.label}
			/>
		</Tooltip>
	);
};

export default MRStatusDot;
